import db from 'serviceWorker/db';
import { ConfigUnavailableReason, hadConfigInThisWorker } from 'serviceWorker/services/config';
import { LogLevel } from 'shared/types/logger';
import { getPullDiagnostics } from './pullDiagnostics';
import { swLog } from './makeSwLogger';

let isLogged = false;

/** How many rows each download-state table of the worker holds: tells a lost database from a new profile. */
async function countServiceRows(): Promise<Record<string, number>> {
  const serviceTables = db.tables.filter(({ name }) => name.endsWith('Service'));
  const counts = await Promise.all(serviceTables.map(async (table) => [table.name, await table.count()] as const));
  return Object.fromEntries(counts);
}

async function isStoragePersisted(): Promise<boolean | null> {
  try {
    return (await navigator.storage?.persisted?.()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Once per worker: a missing config row although the worker holds a token.
 * Nothing in the code removes it, so this tells what the worker found: did it
 * hold a config before (the storage lost it), is the storage persistent, and
 * does any download state exist (a new profile has none).
 */
export async function logConfigNotStoredOnce(reason: ConfigUnavailableReason): Promise<void> {
  try {
    if (isLogged || reason !== ConfigUnavailableReason.configNotStored) {
      return;
    }
    isLogged = true;
    swLog('SW has no stored config', LogLevel.INFO, null, {
      setup: {
        ...getPullDiagnostics(),
        hadConfigInThisWorker: hadConfigInThisWorker(),
        storagePersisted: await isStoragePersisted(),
        dbVersion: db.verno,
        serviceRows: await countServiceRows().catch(() => undefined),
      },
    });
  } catch {
    // Diagnostics only: it runs without being awaited, so it must never fail the pull that asked for it.
  }
}

/** test-only */
export function resetConfigNotStoredLogForTests(): void {
  isLogged = false;
}
